import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Send, CheckCircle, XCircle } from 'lucide-react';
import api from '../../services/api';

interface ReportProblemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ResultState = 'idle' | 'success' | 'error';

export function ReportProblemModal({ open, onOpenChange }: ReportProblemModalProps) {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultState, setResultState] = useState<ResultState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleClose = () => {
    onOpenChange(false);
    setMessage('');
    setResultState('idle');
    setErrorMessage('');
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setIsSubmitting(true);
    try {
      await api.post('/feedback/report-problem', {
        text: message.trim(),
      });

      setResultState('success');
    } catch (error: any) {
      console.error('Failed to submit problem report:', error);
      const errMsg = error.response?.data?.error?.message || 'Ein unbekannter Fehler ist aufgetreten.';
      setErrorMessage(errMsg);
      setResultState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultClose = () => {
    if (resultState === 'success') {
      handleClose();
    } else {
      setResultState('idle');
    }
  };

  return (
    <>
      <Dialog open={open && resultState === 'idle'} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Fehler melden</DialogTitle>
            <DialogDescription>
              Beschreiben Sie das Problem so detailliert wie möglich.
              Wir werden uns so schnell wie möglich darum kümmern.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 px-4">
            <Textarea
              placeholder="Beschreiben Sie das Problem..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[150px] resize-none"
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!message.trim() || isSubmitting}
            >
              <Send className="mr-2 h-4 w-4" />
              {isSubmitting ? 'Wird gesendet...' : 'Senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={resultState === 'success'} onOpenChange={handleResultClose}>
        <DialogContent className="max-w-sm">
          <div className="p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Vielen Dank!</h2>
            <p className="text-muted-foreground mb-4">
              Ihre Fehlermeldung wurde erfolgreich gesendet. Wir werden uns so schnell wie möglich darum kümmern.
            </p>
            <Button onClick={handleResultClose} className="w-full">
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={resultState === 'error'} onOpenChange={handleResultClose}>
        <DialogContent className="max-w-sm">
          <div className="p-6 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Fehler beim Senden</h2>
            <p className="text-muted-foreground mb-4">
              {errorMessage || 'Bitte versuchen Sie es erneut.'}
            </p>
            <Button onClick={handleResultClose} variant="outline" className="w-full">
              Erneut versuchen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
